import { isActivateAccountFunnelEnabled } from '@/config/branding';
import { ORG_ROUTES, PUBLIC_ROUTES } from '@/config/router';
import { useAccountManagerMode } from '@/contexts/AccountManagerContext';

import { useSessionClaims } from './use-session-claims';

/**
 * Platform staff (account manager, super admin, founder) can access merchant
 * accounts even when no billing payment method is on file. Suspended while
 * "View as User" is enabled so staff can still preview the merchant gate.
 */
export function usePrivilegedStaffBypass() {
  const { isAccountManager, isSuperAdmin, isLoaded } = useSessionClaims();
  const { viewAsUser } = useAccountManagerMode();

  const canBypass =
    isLoaded && (isAccountManager || isSuperAdmin) && !viewAsUser;

  return { canBypass, isLoaded };
}

/**
 * Org entry path after auth — dashboard when staff/partner/whitelabel, else activation funnel.
 *
 * The slug is nullable because both signup outcomes can produce a member without one: Clerk's
 * `organization.slug` is optional, and the join-an-existing-org path reads `accounts.org_clerk_slug`,
 * a nullable column (#TAG-1903). Every org route is built as `/org/:slug`, so without a slug there is
 * no org page to send anyone to — the old signature accepted `string` and would have routed a brand
 * new merchant to `/org/null` on their first screen. `/no-organization` is where that state is
 * already resolved: it lists the memberships and can provision the account.
 */
export function getPostAuthOrgPath(orgSlug: string | null | undefined, canBypass: boolean): string {
  if (!orgSlug) {
    return PUBLIC_ROUTES.NO_ORGANIZATION;
  }
  if (canBypass || !isActivateAccountFunnelEnabled()) {
    return ORG_ROUTES(orgSlug).BASE();
  }
  return ORG_ROUTES(orgSlug).ACTIVATE_ACCOUNT();
}
